<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="mb-8">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.ProgressClaimsIndex.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.ProgressClaimsIndex.subTitle') }}
            </p>
          </div>

          <div class="flex items-center space-x-2">
            <button
              v-if="isProjectCompleted"
              type="button"
              class="btn btn--secondary-blue flex items-center space-x-2"
              data-test="export-final-account"
              :disabled="exportFinalAccountAction.is(States.LOADING)"
              @click="exportFinalAccount"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <span>{{ $t('HeadContract.ProgressClaimsIndex.button.exportFinalAccount') }}</span>
            </button>

            <button
              v-if="projectsStore.project?.unarchived && (!latestClaim || latestClaim?.status === ClaimStatus.certified)"
              type="button"
              class="btn btn--secondary-blue flex items-center space-x-2"
              @click.prevent="show = true"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <span>{{ $t('HeadContract.ProgressClaimsIndex.button.newClaim') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="claims.length === 0"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="px-6 py-16 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.ProgressClaimsIndex.noClaimsFound') }}
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            {{ $t('HeadContract.ProgressClaimsIndex.createFirstClaim') }}
          </p>

          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn btn--secondary-blue"
            @click="show = true"
          >
            {{ $t('HeadContract.ProgressClaimsIndex.button.createFirstClaim') }}
          </button>
        </div>
      </div>

      <!-- Main Content Card -->
      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <!-- Card Header -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>


              <h3 class="text-lg font-semibold text-gray-900">
                {{ $t('HeadContract.ProgressClaimsIndex.availableClaims') }}
              </h3>
            </div>

            <Transition
              mode="out-in"
              enter-active-class="animate__animated animate__fadeIn animate__faster"
              leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
              <p
                v-if="claims.length > 0"
                key="total-claims"
                class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
              >
                <span class="font-medium text-gray-900">{{ claims.length }}&nbsp;</span>

                <span class="text-gray-500"> {{ $t('HeadContract.ProgressClaimsIndex.claim', claims.length) }}</span>
              </p>
            </Transition>
          </div>
        </div>

        <!-- Claims List -->
        <div class="divide-y divide-gray-200">
          <div
            v-for="projectClaim in claims"
            :key="projectClaim.id"
            class="p-6 hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer"
            @click="router.push({ name: 'ProgressClaims', params: { claimId: projectClaim.id }})"
          >
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <!-- Claim Details Column -->
              <div class="space-y-3">
                <div class="flex items-center space-x-3">
                  <div class="flex-shrink-0">
                    <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="h-4 w-4 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                    </div>
                  </div>

                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2">
                      <p class="text-sm font-medium text-gray-900">
                        {{ $t('HeadContract.ProgressClaimsIndex.claimNumber', { claimNumber: projectClaim.claim_number }) }}
                      </p>

                      <span
                        v-if="overallStatus(projectClaim.status)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="overallStatus(projectClaim.status).colour"
                      >
                        <span
                          class="w-1.5 h-1.5 rounded-full mr-1.5"
                          :class="overallStatus(projectClaim.status).dot"
                        />
                        {{ overallStatus(projectClaim.status).label }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="text-sm text-gray-600">
                  {{ $t('HeadContract.ProgressClaimsIndex.created', { createdAt: projectClaim.createdAt.toLocaleString() }) }}
                </div>

                <div
                  v-if="projectClaim.status === ClaimStatus.certified"
                  class="text-sm"
                >
                  <span class="text-gray-500">{{ $t('HeadContract.ProgressClaimsIndex.variance') }}:</span>

                  <span class="font-medium text-gray-900 ml-1">
                    {{ australianCurrency(projectClaim.totalVariance) }}
                  </span>
                </div>

                <div
                  v-if="showRetention"
                  class="text-sm"
                >
                  <span class="text-gray-500">{{ $t('HeadContract.ProgressClaimsIndex.retention') }}:</span>

                  <span class="font-medium text-gray-900 ml-1">
                    {{ australianCurrency(projectClaim.retention) }}
                  </span>
                </div>
              </div>

              <!-- Claim Status Column -->
              <div class="space-y-3 border-l border-gray-200 pl-6">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-medium text-gray-900">{{ $t('HeadContract.ProgressClaimsIndex.claim') }}</span>

                  <span
                    v-if="contractorStatus(projectClaim.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="contractorStatus(projectClaim.status).colour"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full mr-1.5"
                      :class="contractorStatus(projectClaim.status).dot"
                    />
                    {{ contractorStatus(projectClaim.status).label }}
                  </span>
                </div>

                <div
                  v-if="projectClaim.draftIssuedAt"
                  class="text-sm text-gray-600"
                >
                  {{ $t('HeadContract.ProgressClaimsIndex.received', { receivedAt: projectClaim.draftIssuedAt.toLocaleString() }) }}
                </div>

                <div
                  v-if="projectClaim.claimIssuedAt"
                  class="text-sm text-gray-600"
                >
                  {{ $t('HeadContract.ProgressClaimsIndex.submitted', { submittedAt: projectClaim.claimIssuedAt.toLocaleString() }) }}
                </div>

                <div
                  v-if="projectClaim.claimIssuedAt"
                  class="text-sm"
                >
                  <span class="text-gray-500">{{ $t('HeadContract.ProgressClaimsIndex.amount') }}:</span>

                  <span class="font-medium text-gray-900 ml-1">
                    {{ australianCurrency(projectClaim.totalClaim) }}
                  </span>
                </div>
              </div>

              <!-- Certificate Status Column -->
              <div class="space-y-3 border-l border-gray-200 pl-6">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-medium text-gray-900">{{ $t('HeadContract.ProgressClaimsIndex.certificate') }}</span>

                  <span
                    v-if="pqsStatus(projectClaim.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="pqsStatus(projectClaim.status).colour"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full mr-1.5"
                      :class="pqsStatus(projectClaim.status).dot"
                    />
                    {{ pqsStatus(projectClaim.status).label }}
                  </span>
                </div>

                <div
                  v-if="projectClaim.claimIssuedAt"
                  class="text-sm text-gray-600"
                >
                  {{ $t('HeadContract.ProgressClaimsIndex.received', { receivedAt: projectClaim.claimIssuedAt.toLocaleString() }) }}
                </div>

                <div
                  v-if="projectClaim.submittedAt"
                  class="text-sm text-gray-600"
                >
                  {{ $t('HeadContract.ProgressClaimsIndex.submitted', { submittedAt: projectClaim.submittedAt.toLocaleString() }) }}
                </div>

                <div
                  v-if="projectClaim.status === ClaimStatus.certified"
                  class="text-sm"
                >
                  <span class="text-gray-500">{{ $t('HeadContract.ProgressClaimsIndex.amount') }}:</span>

                  <span class="font-medium text-gray-900 ml-1">
                    {{ australianCurrency(projectClaim.totalCertified) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Retention Summary -->
        <div
          v-if="showRetention && claims.length > 0"
          class="p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <h3 class="text-lg font-semibold text-gray-900">
                {{ $t('HeadContract.ProgressClaimsIndex.totalRetention') }}
              </h3>
            </div>

            <p class="text-lg font-semibold text-gray-900">
              {{ australianCurrency(totalRetention) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <CreateClaimModal
      v-if="show"
      :project-id="$props.projectId"
      boq-type="direct"
      @close="show = false"
    />
  </div>
</template>

<script setup lang="ts">
import CreateClaimModal from '@/components/ProgressClaims/CreateClaimModal.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, enumKeyFromValue } from '@/helpers';
import { t } from '@/i18n';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const router = useRouter();
const projectsStore = useProjectsStore();
const userStore = useUserStore();

watch(() => [props.projectId], () => {
  claimsStore.fetchClaims(props.projectId, { boq_type: 'direct' });
  tradesStore.fetchTrades(props.projectId, { boq_type: 'direct' });
}, { immediate: true });

const show = ref(false);

const claims = computed(() => {
  return claimsStore.models.where('project_id', parseInt(props.projectId)).where('boq_type', 'direct').orderBy(
    'claim_number',
    'desc',
  ).orderBy(
    'claim_number',
    'desc',
  ).get();
});

const overallStatus = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (true) {
    case status === ClaimStatus.certified:
      return {
        colour: 'bg-green-100 text-green-800',
        label: t('Models.Claim.statusTitles.certified'),
        dot: 'bg-green-400',
      };

    default:
      return {
        colour: 'bg-red-100 text-red-800',
        label: t('Models.Claim.statusTitles.draft'),
        dot: 'bg-red-400',
      };
  }
};

const contractorStatus = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (true) {
    case status === ClaimStatus.draft_pqs:

      // Fall through
    case status === ClaimStatus.draft:
      return {
        colour: 'bg-red-100 text-red-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.notIssued'),
        dot: 'bg-red-400',
      };

    case status === ClaimStatus.contractor_claim:
      return {
        colour: 'bg-orange-100 text-orange-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.issued'),
        dot: 'bg-orange-400',
      };

    case status === ClaimStatus.pqs_review:

      // Fall through
    case status === ClaimStatus.certified:
      return {
        colour: 'bg-green-100 text-green-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.submitted'),
        dot: 'bg-green-400',
      };

    default:
      return undefined;
  }
};

const pqsStatus = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (true) {
    case status === ClaimStatus.draft:
      return {
        colour: 'bg-red-100 text-red-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.notIssued'),
        dot: 'bg-red-400',
      };

    case status === ClaimStatus.pqs_review:
      return {
        colour: 'bg-orange-100 text-orange-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.issued'),
        dot: 'bg-orange-400',
      };

    case status === ClaimStatus.certified:
      return {
        colour: 'bg-green-100 text-green-800',
        label: t('HeadContract.ProgressClaimsIndex.claimStatus.submitted'),
        dot: 'bg-green-400',
      };

    default:
      return undefined;
  }
};

const latestClaim = computed(() => {
  if(claims.value.length) {
    return claims.value[0];
  }

  return undefined;
});

const showRetention = computed(() => {
  return projectsStore.project?.head_contract_settings?.performance_security === 'retention';
});

const totalRetention = computed(() => {
  return claims.value.reduce((total, claim) => {
    return total + (claim.retention ?? 0);
  }, 0);
});

const exportFinalAccountAction = useStoreApiAction(claimsStore.exportFinalAccount);

const exportFinalAccount = () => {
  exportFinalAccountAction.request(props.projectId).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Statement of Final Account exported.',
      text: 'Statement of Final Account is being exported to PDF and will be sent to your email when it is ready.',
    });
  });
};

const isProjectCompleted = computed(() => {
  return projectsStore.project?.contractEndDate < new Date();
});
</script>

<style scoped></style>
